import React, { useState, useEffect } from "react";
import { Row, Col, Form, Label, Input, ButtonGroup, FormGroup } from "reactstrap";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { is_empty, is_integer } from "../../config/validate";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import {
  cleanDataFile,
  saveFile,
  updateFile,
  getMaterialList,
} from "../../store/actions";
import LinearIndeterminate from "../../components/Common/LinearIndeterminate";
import showToast from "../../components/Common/viewNotification";
import AsyncSelect from "react-select/async";
import service from "../../api/monitor.service";

const FileRequirementForm = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { onClosing, currentFile, onLoad, fileTypeList } = props;

  const [id, setId] = useState({ value: "", error: "" });
  const [fileName, setFileName] = useState({ value: "", error: "" });
  const [isEdit, setIsEdit] = useState(false);
  const [mode, setMode] = useState("general");

  const [supplierSingle, setSupplierSingle] = useState(null);
  const [suppliersMulti, setSuppliersMulti] = useState([]);
  const [supplierError, setSupplierError] = useState("");

  const [materials, setMaterials] = useState([]);
  const [materialsError, setMaterialsError] = useState("");

  const [reqTypeFile, setReqTypeFile] = useState({ value: "", label: "", error: "" });
  const [updateFrequency, setUpdateFrequency] = useState({ value: "", label: "", error: "" });

  const [auth] = useState(JSON.parse(sessionStorage.getItem("user")));
  const [errors, setErrors] = useState([]);

  const {
    loadingSave,
    response,
    error,
    materialList,
    loadingSupplier,
    loading,
    materialLoading,
  } = useSelector(state => ({
    loadingSave: state.fileRequirement.loadingSave,
    response: state.fileRequirement.response,
    error: state.fileRequirement.error,
    responseSupplier: state.materialSupplier.response,
    loading: state.materialSupplier.loadingSave,
    materialList: state.materialSupplier.materialList,
    materialLoading: state.materialSupplier.loading,
  }));

  useEffect(() => {
    if (!is_empty(error)) {
      showToast(error, "error", t("error:title"));
      dispatch(cleanDataFile());
    }
  }, [error, dispatch, t]);

  useEffect(() => {
    if (response !== null) {
      showToast(t("global:msgSuccessSaveList"), "success", t("global:config"));
      clearForm();
      onClosing(true);
      dispatch(cleanDataFile());
      onLoad();
    }
  }, [response, onClosing, dispatch, onLoad, t]);

  useEffect(() => {
    if (currentFile) {
      setData(currentFile);
    } else {
      clearForm();
    }
  }, [currentFile]);

  const handleSubmit = async () => {
    const isValid = validate();
    if (!isValid) return;

    const values = {
      id: id.value,
      fileName: fileName.value,
      requirementTypeFile: { id: reqTypeFile.value },
      updateFrequency: updateFrequency.value,
      createDate: moment().format("YYYY-MM-DD"),
      createdBy: auth?.id?.user?.username,
      status: 1,
      ...(mode === "material"
        ? {
          supplierIds: supplierSingle?.value ? [supplierSingle.value] : [],
          materialId: materials.length > 0 ? materials.map(m => m.value) : null,
        }
        : {
          supplierIds: suppliersMulti.map(s => s.value),
          materialId: null,
        }),
    };

    if (values.id) {
      await dispatch(updateFile(values));
    } else {
      await dispatch(saveFile(values));
    }
  };

  const validate = () => {
    const newErrors = [];

    const reqTypeFileError =
      !is_integer(reqTypeFile.value) || reqTypeFile.value === 0 ? t("error:required") : "";

    const updateFrequencyError = is_empty(updateFrequency.value) ? t("error:required") : "";
    const fileNameError = is_empty(fileName.value) ? t("error:required") : "";

    setReqTypeFile(prev => ({ ...prev, error: reqTypeFileError }));
    setUpdateFrequency(prev => ({ ...prev, error: updateFrequencyError }));
    setFileName(prev => ({ ...prev, error: fileNameError }));

    if (reqTypeFileError) newErrors.push(reqTypeFileError);
    if (updateFrequencyError) newErrors.push(updateFrequencyError);
    if (fileNameError) newErrors.push(fileNameError);

    if (!isEdit) {
      if (mode === "material") {
        if (!supplierSingle?.value) {
          setSupplierError(t("error:required"));
          newErrors.push(t("error:required"));
        } else {
          setSupplierError("");
        }

        if (materials.length === 0) {
          setMaterialsError(t("error:required"));
          newErrors.push(t("error:required"));
        } else {
          setMaterialsError("");
        }
      } else {
        if (!Array.isArray(suppliersMulti) || suppliersMulti.length === 0) {
          setSupplierError(t("error:required"));
          newErrors.push(t("error:required"));
        } else {
          setSupplierError("");
        }
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const clearForm = () => {
    setId({ value: "", error: "" });
    setReqTypeFile({ value: "", label: "", error: "" });
    setUpdateFrequency({ value: "", error: "" });
    setFileName({ value: "", label: "", error: "" });
    setSupplierSingle(null);
    setSuppliersMulti([]);
    setMaterials([]);
    setMaterialsError("");
    setSupplierError("");
    setIsEdit(false);
    setMode("general");
    setErrors([]);
  };

  const setData = file => {
    const hasSingleSupplier = !!file?.supplier;
    const hasMaterial = !!file?.material;

    setIsEdit(true);

    setId({ value: file?.id || "", error: "" });
    setUpdateFrequency({ value: file?.updateFrequency || "", error: "" });
    setFileName({ value: file?.fileName || "", error: "" });
    setReqTypeFile({
      value: file?.requirementTypeFile?.id || "",
      label: file?.requirementTypeFile?.name || "",
      error: "",
    });

    if (hasSingleSupplier) {
      setSupplierSingle({
        value: file.supplier.id,
        label: `${file.supplier.name} | ${file.supplier.code}`,
        name: file.supplier.name,
        code: file.supplier.code,
        id: file.supplier.id,
      });
    } else {
      setSupplierSingle(null);
    }

    if (hasMaterial) {
      setMaterials([
        {
          value: file.material.id,
          label: `${file.material.name} | ${file.material.codeSap || ""}`,
          name: file.material.name,
        },
      ]);
    } else {
      setMaterials([]);
    }

    if (hasSingleSupplier || hasMaterial) {
      setMode("material");
      setSuppliersMulti([]);
    } else if (Array.isArray(file?.suppliers) && file.suppliers.length > 0) {
      setMode("general");
      setSuppliersMulti(
        file.suppliers.map(item => ({
          value: item.id,
          label: `${item.name} | ${item.code}`,
          name: item.name,
          code: item.code,
          id: item.id,
        }))
      );
      setSupplierSingle(null);
      setMaterials([]);
    } else {
      setMode("general");
      setSupplierSingle(null);
      setSuppliersMulti([]);
      setMaterials([]);
    }

    setSupplierError("");
    setMaterialsError("");
  };

  const requirementTypeList = () => {
    if (fileTypeList?.length > 0) {
      return fileTypeList.map(item => ({ value: item.id, label: item.name }));
    }
    return [];
  };

  const changeRequirementTypeList = value => {
    if (value === null) {
      setReqTypeFile({ value: "", label: "", error: "" });
    } else {
      setReqTypeFile({ value: value.value, label: value.label, error: "" });
    }
  };

  const loadMaterialsForSupplier = async supplierOption => {
    const body = {
      length: 1000,
      page: 0,
      supplierId: supplierOption?.value,
    };
    await dispatch(getMaterialList(body));
  };

  const loadSuppliers = filter => {
    if (auth) {
      const json = {
        username: auth?.id?.user?.username,
        length: 30,
        filter,
        societyCode: process.env.REACT_APP_STYLE_TEMPLATE,
      };
      return service.getSupplier(json);
    }
  };

  const loadingOptionSupplier = async (filter, callback) => {
    if ((filter ?? "").length >= 3) {
      const data = await loadSuppliers(filter);
      const s = data?.data?.map(item => ({
        ...item,
        value: item.id,
        code: item.code,
        label: `${item.name} | ${item.code}`,
      }));
      callback(s || []);
    } else {
      callback([]);
    }
  };

  const materialOptions =
    materialList.materialSupplierList?.map(item => ({
      value: item.id,
      label: item.name,
    })) ?? [];

  const onChangeMaterial = selectedList => {
    if (isEdit) return;
    const safeList = Array.isArray(selectedList) ? selectedList : [];
    setMaterials(safeList);
    setMaterialsError("");
  };

  const onChangeSupplier = async value => {
    if (isEdit) return;

    if (mode === "material") {
      setSupplierSingle(value || null);
      setMaterials([]);
      setMaterialsError("");

      if (value?.value) {
        await loadMaterialsForSupplier(value);
      }
    } else {
      setSuppliersMulti(value || []);
    }

    setSupplierError("");
  };

  return (
    <React.Fragment>
      <Form>
        {!isEdit && (
          <ButtonGroup className="mb-3">
            <button
              type="button"
              className={`btn ${mode === "general" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setMode("general")}
            >
              {t("files:tabGeneral")}
            </button>
            <button
              type="button"
              className={`btn ${mode === "material" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setMode("material")}
            >
              {t("files:tabSku")}
            </button>
          </ButtonGroup>
        )}

        <Row>
          <Col xs={12} md={mode === "material" ? 6 : 12}>
            <div className="mb-2">
              <Form className="row gy-2 gx-3 align-items-center">
                <FormGroup className="mb-0 w-100">
                  <Label>{t("materialSupplier:titleSupplier")}</Label>
                  <div className="d-flex flex-column gap-2">
                    <AsyncSelect
                      loadOptions={loadingOptionSupplier}
                      defaultOptions={false}
                      maxMenuHeight={150}
                      cacheOptions
                      className="small-font text-uppercase"
                      isDisabled={loading || isEdit}
                      placeholder={t("monitor:supplierFind")}
                      name="supplier"
                      isClearable={!isEdit}
                      isMulti={mode !== "material"}
                      isLoading={loadingSupplier}
                      onChange={onChangeSupplier}
                      value={mode === "material" ? supplierSingle : suppliersMulti}
                    />
                    <span className="small text-danger">{supplierError}</span>
                  </div>
                </FormGroup>
              </Form>
            </div>
          </Col>

          {mode === "material" && (
            <Col xs={12} md={6}>
              <div className="mb-2">
                <Label>{t("filesUpload:lblMaterial")}</Label>
                {materialLoading && !isEdit ? (
                  <LinearIndeterminate />
                ) : (
                  <Select
                    options={isEdit ? materials : materialOptions}
                    value={materials}
                    onChange={onChangeMaterial}
                    isDisabled={isEdit}
                    isMulti
                    closeMenuOnSelect={false}
                    placeholder={t("monitor:supplierFind")}
                  />
                )}
                <span className="small text-danger">{materialsError}</span>
              </div>
            </Col>
          )}
        </Row>

        <Row>
          <Col xs={12}>
            <div className="mb-2">
              <Label htmlFor="name">{t("files:titleNameFile")}</Label>
              <Input
                autoComplete="new-text"
                type="textarea"
                className="form-control"
                value={fileName.value || ""}
                onChange={e => {
                  setFileName({ value: e.target.value, error: "" });
                }}
                invalid={!is_empty(fileName.error)}
              />
              <span className="small text-danger">{fileName.error}</span>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={12}>
            <div className="mb-2">
              <Label htmlFor="name">{t("files:titleUpdateFrequency")}</Label>
              <Input
                autoComplete="new-text"
                type="textarea"
                className="form-control"
                value={updateFrequency.value || ""}
                onChange={e => {
                  setUpdateFrequency({ value: e.target.value, error: "" });
                }}
                invalid={!is_empty(updateFrequency.error)}
              />
              <span className="small text-danger">{updateFrequency.error}</span>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs={6}>
            <div className="mb-2">
              <Label htmlFor="name">{t("files:titleTypeReqFile")}</Label>
              <Select
                name="typeFile"
                options={requirementTypeList()}
                value={reqTypeFile}
                onChange={value => changeRequirementTypeList(value)}
              />
              <span className="small text-danger">{reqTypeFile.error}</span>
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="modal-footer">
              <ButtonGroup>
                <button
                  type="button"
                  className="btn btn-info save-user"
                  onClick={handleSubmit}
                >
                  <i className="fas fa-save me-1" />
                  {t("global:save")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={loadingSave}
                  onClick={() => {
                    onClosing(true);
                  }}
                >
                  {t("global:close")}
                </button>
              </ButtonGroup>
            </div>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  );
};

export default withRouter(FileRequirementForm);